import { useEffect, useState } from 'react';
import {
  connectCalcomFullPageRedirect,
  disconnectCalcom,
  getCalcomConflictingIntegration,
  getCalcomConnectionStatus,
} from 'src/services/client';
import {
  ICalcomApiResult,
  ICalcomConflictingIntegration,
  ICalcomConnectedCalendar,
  ICalcomConnectionStatus,
  ICalcomStartAuthorization,
} from 'src/pages/Calendar/CalcomIntegration/types';

const CALCOM_RESULT_PARAM = 'calcom';
const CALCOM_REASON_PARAM = 'reason';
const CALCOM_CONFLICT_INTEGRATION_ID = 'conflictIntegrationId';

const CALCOM_CANCELLATION_REASONS = new Set<string>(['access_denied']);

/* The account is already linked to another Juniper user. The conflict card renders the full
 * explanation from the looked-up owner, so we don't also surface the raw reason as a generic error. */
const CALCOM_CONFLICT_REASON = 'account_already_connected';

export interface IUseCalcomConnection {
  status: ICalcomConnectionStatus | null;
  isConnected: boolean;
  isLoadingStatus: boolean;
  statusError: string | null;

  isConnecting: boolean;
  connectError: string | null;
  connectViaFullPageRedirect: () => Promise<void>;

  connectedCalendars: ICalcomConnectedCalendar[];

  conflictingIntegration: ICalcomConflictingIntegration | null;

  disconnect: () => Promise<void>;
}

export const useCalcomOauthConnection = (): IUseCalcomConnection => {
  const [status, setStatus] = useState<ICalcomConnectionStatus | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState<boolean>(true);
  const [statusError, setStatusError] = useState<string | null>(null);

  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [connectError, setConnectError] = useState<string | null>(null);

  const [conflictingIntegration, setConflictingIntegration] =
    useState<ICalcomConflictingIntegration | null>(null);

  const isConnected: boolean = status?.connected ?? false;

  const connectedCalendars: ICalcomConnectedCalendar[] =
    status?.connectedCalendars ?? [];

  const fetchStatus = async (): Promise<void> => {
    setIsLoadingStatus(true);
    setStatusError(null);

    const result: ICalcomApiResult<ICalcomConnectionStatus> =
      await getCalcomConnectionStatus();

    if (!result.ok) {
      setStatusError(
        result.error ?? 'Failed to fetch Cal.com connection status'
      );

      setIsLoadingStatus(false);
      return;
    }

    setStatus(result.data ?? null);
    setIsLoadingStatus(false);
  };

  const connectViaFullPageRedirect = async (): Promise<void> => {
    setIsConnecting(true);
    setConnectError(null);

    const returnOrigin: string = window.location.origin + window.location.pathname;

    const result: ICalcomApiResult<ICalcomStartAuthorization> =
      await connectCalcomFullPageRedirect(returnOrigin);

    if (!result.ok || !result.data?.authorizeUrl) {
      setConnectError(result.error ?? 'Failed to start Cal.com connection');
      setIsConnecting(false);
      return;
    }

    window.location.href = result.data.authorizeUrl;
  };

  const disconnect = async (): Promise<void> => {
    const result = await disconnectCalcom();

    if (!result.ok) {
      setStatusError(result.error ?? 'Failed to disconnect Cal.com account');
      return;
    }

    await fetchStatus();
  };

  const resolveConflictingIntegration = async (
    integrationId: string
  ): Promise<void> => {
    const conflict: ICalcomApiResult<ICalcomConflictingIntegration> =
      await getCalcomConflictingIntegration(integrationId);

    if (!conflict.ok) {
      setConnectError(
        conflict.error ?? 'Failed to fetch the conflicting Cal.com account'
      );
      return;
    }

    setConflictingIntegration(conflict.data ?? null);
  };

  /* After the OAuth bounce the backend redirects here with ?calcom=connected|error&reason=…
   * Read it once, surface any failure, look up who owns the seat on a conflict, then strip the
   * params so a refresh can't re-process them. */
  const consumeRedirectResult = async (): Promise<void> => {
    const params: URLSearchParams = new URLSearchParams(window.location.search);
    const result: string = params.get(CALCOM_RESULT_PARAM);

    if (!result) {
      return;
    }

    const reason: string = params.get(CALCOM_REASON_PARAM);
    const wasCancelled: boolean = reason !== null && CALCOM_CANCELLATION_REASONS.has(reason);

    const conflictIntegrationId: string | null = params.get(
      CALCOM_CONFLICT_INTEGRATION_ID
    );

    const isConflict: boolean = reason === CALCOM_CONFLICT_REASON;

    if (result === 'error' && !wasCancelled && !isConflict) {
      setConnectError(reason ?? 'Cal.com connection failed');
    }

    if (conflictIntegrationId) {
      await resolveConflictingIntegration(conflictIntegrationId);
    }

    params.delete(CALCOM_RESULT_PARAM);
    params.delete(CALCOM_REASON_PARAM);
    params.delete(CALCOM_CONFLICT_INTEGRATION_ID);

    const cleanedQuery = params.toString();

    const cleanedUrl: string =
      window.location.pathname + (cleanedQuery ? `?${cleanedQuery}` : '');
    window.history.replaceState({}, '', cleanedUrl);
  };

  useEffect(() => {
    void consumeRedirectResult();
    void fetchStatus();
  }, []);

  return {
    status,
    isConnected,
    isLoadingStatus,
    statusError,
    isConnecting,
    connectError,
    connectViaFullPageRedirect,
    connectedCalendars,
    conflictingIntegration,
    disconnect,
  };
};